import React from 'react';
import type { ReactElement } from 'react';
import { Dialog, Modal, Button, Inline, H2 } from '@construct-kit/core';
import styled, { css } from 'styled-components';
import { AllFilters } from '../../../../shared/components/AllFilters';
import type { SelectedFiltersOptionsType } from '../../../../shared/model/constants/filterTypes';
import { useLockBodyScroll } from './useLockBodyScroll';
import { DEFAULT_FILTERS_REFINEMENTS_OPTIONS } from './mapRefinementsFilters';

export type FilterDialogProps = {
  isOpen: boolean;
  appElement: () => string | HTMLElement;
  onClose: () => void;
  initialFilterOptions: SelectedFiltersOptionsType;
  selectedFiltersOptions: SelectedFiltersOptionsType;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  handleSearchOnClick: (onClose?: () => void) => void;
  shouldHideHeader?: boolean;
};

const StyledDialog = styled(Dialog)`
  ${({ theme }) => css`
    ${theme.layoutBreakpoints.large} {
      width: 46rem;
    }
  `}
  overflow-y: hidden;
`;

export const FilterDialog = ({
  isOpen,
  appElement,
  onClose,
  initialFilterOptions,
  selectedFiltersOptions,
  setSelectedFiltersOptions,
  handleSearchOnClick,
}: FilterDialogProps): ReactElement => {
  const handleClearOnclick = (): void =>
    setSelectedFiltersOptions({
      channel: selectedFiltersOptions.channel,
      searchLocations: selectedFiltersOptions.searchLocations,
      ...DEFAULT_FILTERS_REFINEMENTS_OPTIONS,
    });

  const handleModalOnClose = (): void => {
    setSelectedFiltersOptions(initialFilterOptions);
    onClose();
  };

  useLockBodyScroll();

  return (
    <Modal opened={isOpen} onRequestClose={handleModalOnClose} getAppElement={appElement}>
      <StyledDialog
        noMargins
        onClose={handleModalOnClose}
        headerAlignment="center"
        autoFullScreen
        fullScreenMediaQuery="(max-width: 47.9375rem)"
        maxWidth="46rem"
        header={<H2 variant="title01">Filters</H2>}
        footer={
          <Inline justifyContent="space-between" grow={false}>
            <Button variant="naked" sizeVariant="medium" onClick={handleClearOnclick}>
              Clear all filters
            </Button>
            <Button
              onClick={() => {
                handleSearchOnClick(onClose);
              }}
              sizeVariant="medium"
            >
              Search
            </Button>
          </Inline>
        }
      >
        <AllFilters
          initialFilterOptions={initialFilterOptions}
          selectedFiltersOptions={selectedFiltersOptions}
          setSelectedFiltersOptions={setSelectedFiltersOptions}
        />
      </StyledDialog>
    </Modal>
  );
};

export default FilterDialog;
